import { jsPDF } from 'jspdf';

// ─── Fix de acentos (mesmo padrão usado nos outros PDFs do sistema) ───────────
function safe(text) {
  if (!text) return '';
  return String(text)
    .replace(/[àáâãä]/g, 'a').replace(/[ÀÁÂÃÄ]/g, 'A')
    .replace(/[èéêë]/g,  'e').replace(/[ÈÉÊË]/g,  'E')
    .replace(/[ìíîï]/g,  'i').replace(/[ÌÍÎÏ]/g,  'I')
    .replace(/[òóôõö]/g, 'o').replace(/[ÒÓÔÕÖ]/g, 'O')
    .replace(/[ùúûü]/g,  'u').replace(/[ÙÚÛÜ]/g,  'U')
    .replace(/[ç]/g,     'c').replace(/[Ç]/g,     'C')
    .replace(/[ñ]/g,     'n').replace(/[Ñ]/g,     'N')
    .replace(/[–—]/g,    '-')
    .replace(/['']/g,    "'")
    .replace(/[""]/g,    '"')
    .replace(/[^\x00-\xFF]/g, '');
}

// ─── Paleta — idêntica ao restante do sistema ──────────────────────────────────
const COLORS = {
  primary:     [22,  58, 114],
  accent:      [37, 137, 210],
  accentLight: [90, 170, 230],
  light:       [240, 246, 255],
  lightAlt:    [248, 251, 255],
  dark:        [20,  20,  20],
  muted:       [110, 118, 135],
  white:       [255, 255, 255],
  border:      [210, 220, 235],
  success:     [39, 174,  96],   // paga
  warning:     [230, 160,   0],  // pendente
  danger:      [200,  50,  50],  // cancelada
  accentBlue:  [37, 137, 210],   // enviada
  purple:      [124, 58, 237],   // confirmada
};

// Mapa status → cor (mesmo padrão visual do resto do sistema)
const STATUS_COLOR = {
  pendente:   COLORS.warning,
  enviada:    COLORS.accentBlue,
  confirmada: COLORS.purple,
  paga:       COLORS.success,
  cancelada:  COLORS.danger,
};

const STATUS_LABEL = {
  pendente:   'Pendente',
  enviada:    'Enviada',
  confirmada: 'Confirmada',
  paga:       'Paga',
  cancelada:  'Cancelada',
};

function setColor(doc, color, target = 'text') {
  if (target === 'text') doc.setTextColor(...color);
  else if (target === 'fill') doc.setFillColor(...color);
  else if (target === 'draw') doc.setDrawColor(...color);
}

function sectionHeader(doc, title, y, pageW, margin = 40) {
  const h = 24;
  setColor(doc, COLORS.accent, 'fill');
  doc.rect(margin, y, 4, h, 'F');
  setColor(doc, COLORS.light, 'fill');
  doc.rect(margin + 4, y, pageW - margin * 2 - 4, h, 'F');
  setColor(doc, COLORS.border, 'draw');
  doc.setLineWidth(0.3);
  doc.line(margin, y + h, pageW - margin, y + h);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  setColor(doc, COLORS.primary);
  doc.text(safe(title).toUpperCase(), margin + 12, y + 16);
  return y + h + 14;
}

function fmtCurrency(value) {
  return `R$ ${Number(value || 0).toFixed(2)}`;
}

/**
 * Gera e baixa um PDF com as estatísticas de notas por status.
 * @param {Object} statsData - retorno de getStatistics: { scope, year, month, statistics, totalCount, totalValue }
 * @param {Object} user      - usuário logado (nome, cnpj, image, etc.)
 */
export async function generateStatisticsPdf(statsData, user) {
  if (!statsData || !statsData.statistics) return;

  const { scope, year, month, statistics, totalCount, totalValue } = statsData;

  const doc    = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW  = doc.internal.pageSize.getWidth();
  const pageH  = doc.internal.pageSize.getHeight();
  const margin = 40;
  const colMid = pageW / 2;

  const MONTHS = [
    "Janeiro","Fevereiro","Marco","Abril","Maio","Junho",
    "Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"
  ];

  // ── Período em texto legível ──
  let periodLabel = 'Todo o periodo';
  if (scope === 'year' && year)  periodLabel = `Ano de ${year}`;
  if (scope === 'month' && year && month) periodLabel = `${MONTHS[month - 1]} de ${year}`;

  // ══════════════════════════════════════════════════════════════════════
  // CABEÇALHO
  // ══════════════════════════════════════════════════════════════════════
  const headerH = 72;

  setColor(doc, COLORS.primary, 'fill');
  doc.rect(0, 0, pageW, headerH, 'F');
  setColor(doc, COLORS.accent, 'fill');
  doc.rect(0, headerH - 4, pageW, 4, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  setColor(doc, COLORS.white);
  doc.text('ESTATISTICAS DE NOTAS', margin, 36);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(13);
  setColor(doc, COLORS.accentLight);
  doc.text(safe(periodLabel), margin, 54);

  if (user?.name) {
    doc.setFontSize(10);
    setColor(doc, [180, 200, 230]);
    doc.text(safe(user.name), margin, 68);
  }

  let y = headerH + 16;

  // ══════════════════════════════════════════════════════════════════════
  // RESUMO GERAL
  // ══════════════════════════════════════════════════════════════════════
  y = sectionHeader(doc, 'Resumo Geral', y, pageW);

  const tableW = pageW - margin * 2;
  const boxW   = (tableW - 10) / 2;

  // Box: Total de notas
  setColor(doc, COLORS.lightAlt, 'fill');
  setColor(doc, COLORS.border, 'draw');
  doc.setLineWidth(0.4);
  doc.roundedRect(margin, y, boxW, 40, 3, 3, 'FD');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  setColor(doc, COLORS.muted);
  doc.text('TOTAL DE NOTAS', margin + 12, y + 15);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  setColor(doc, COLORS.dark);
  doc.text(String(totalCount || 0), margin + 12, y + 32);

  // Box: Valor total
  setColor(doc, COLORS.lightAlt, 'fill');
  setColor(doc, COLORS.border, 'draw');
  doc.roundedRect(margin + boxW + 10, y, boxW, 40, 3, 3, 'FD');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  setColor(doc, COLORS.muted);
  doc.text('VALOR TOTAL (TODAS)', margin + boxW + 22, y + 15);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  setColor(doc, COLORS.primary);
  doc.text(fmtCurrency(totalValue), margin + boxW + 22, y + 32);

  y += 56;

  // ══════════════════════════════════════════════════════════════════════
  // DETALHAMENTO POR STATUS
  // ══════════════════════════════════════════════════════════════════════
  y = sectionHeader(doc, 'Detalhamento por Status', y, pageW);

  const statusKeys = ['pendente', 'enviada', 'confirmada', 'paga', 'cancelada'];
  const rowH = 34;

  statusKeys.forEach((key, i) => {
    const data  = statistics[key] || { count: 0, total: 0 };
    const color = STATUS_COLOR[key];

    const isEven = i % 2 === 0;
    setColor(doc, isEven ? COLORS.white : COLORS.lightAlt, 'fill');
    doc.rect(margin, y, tableW, rowH, 'F');

    setColor(doc, COLORS.border, 'draw');
    doc.setLineWidth(0.2);
    doc.rect(margin, y, tableW, rowH);

    // Faixa lateral colorida (igual ao badge de status do sistema)
    setColor(doc, color, 'fill');
    doc.rect(margin, y, 5, rowH, 'F');

    // Label do status
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    setColor(doc, COLORS.dark);
    doc.text(safe(STATUS_LABEL[key]).toUpperCase(), margin + 16, y + 16);

    // Quantidade
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    setColor(doc, COLORS.muted);
    doc.text(`${data.count} nota${data.count !== 1 ? 's' : ''}`, margin + 16, y + 28);

    // Barra de proporção visual
    const maxBarW = 160;
    const barW = totalCount > 0 ? (data.count / totalCount) * maxBarW : 0;
    const barX = margin + 220;
    const barY = y + 12;

    setColor(doc, COLORS.border, 'fill');
    doc.roundedRect(barX, barY, maxBarW, 8, 2, 2, 'F');

    if (barW > 0) {
      setColor(doc, color, 'fill');
      doc.roundedRect(barX, barY, Math.max(barW, 4), 8, 2, 2, 'F');
    }

    // Percentual
    const pct = totalCount > 0 ? ((data.count / totalCount) * 100).toFixed(0) : 0;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    setColor(doc, COLORS.muted);
    doc.text(`${pct}%`, barX + maxBarW + 8, y + 17);

    // Valor total do status
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    setColor(doc, COLORS.dark);
    doc.text(fmtCurrency(data.total), pageW - margin - 12, y + 21, { align: 'right' });

    y += rowH;
  });

  y += 16;

  // ══════════════════════════════════════════════════════════════════════
  // RODAPÉ
  // ══════════════════════════════════════════════════════════════════════
  setColor(doc, COLORS.accent, 'fill');
  doc.rect(0, pageH - 40, pageW, 2, 'F');
  setColor(doc, COLORS.light, 'fill');
  doc.rect(0, pageH - 38, pageW, 38, 'F');

  const now = new Date();
  const footerLeft  = safe(user?.name || '');
  const footerRight = `Gerado em ${now.toLocaleDateString('pt-BR')} as ${now.toLocaleTimeString('pt-BR')}`;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  setColor(doc, COLORS.primary);
  if (footerLeft) doc.text(footerLeft, margin, pageH - 18);

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8.5);
  setColor(doc, COLORS.muted);
  doc.text(footerRight, pageW - margin, pageH - 18, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  setColor(doc, COLORS.muted);
  doc.text('Pagina 1 de 1', colMid, pageH - 10, { align: 'center' });

  // ══════════════════════════════════════════════════════════════════════
  // DOWNLOAD
  // ══════════════════════════════════════════════════════════════════════
  const fileName = `estatisticas-${scope || 'all'}-${year || ''}${month ? '-' + month : ''}.pdf`;
  const blob = doc.output('blob');
  const url  = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}